import React, { Component } from 'react';
import { browserHistory } from 'react-router';
import { Form } from 'redux-form';
import idx from 'idx';
import omit from 'lodash/omit';
import PageTransition from 'react-router-page-transition';
import { getSubCategory, getCategory } from 'common/ducks/categories';
import { getCurrentStepIndex, getFieldPropsFromDefinition } from 'pap/utils';
import { getBannerContents } from 'common/components/discount_code';
import { getStoredDiscountCode, getPendingDiscountCode } from 'pap/ducks/pricing';
import PAPQuestionBase from 'pap/components/common/pap_question_base';
import PAPBase from 'pap/containers/common/pap_base';
import { getVisibleBriefQuestions, getPapUrl } from 'pap_v2/utils';
import { LAZY_REGISTRATION_STEP_IDX } from 'pap_v2/constants';
import { trackGoogle, trackPerfectAudience, trackFacebookCustomEvent,
  trackMixpanel, trackClickWithMixpanel, MP_CHANGED_PAP_STEP_EVENT_NAME,
  MP_CHANGED_PAP_SECTION_EVENT_NAME
} from 'common/tracking';


/**
 * A component responsible for rendering all brief question screens.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class Brief extends Component {
  constructor(props) {
    super(props);

    this.getPapUrl = this.getPapUrl.bind(this);
    this.getCurrentStepIndex = this.getCurrentStepIndex.bind(this);
    this.getPrevStepLink = this.getPrevStepLink.bind(this);
    this.getNextStepLink = this.getNextStepLink.bind(this);
    this.redirectIfNeeded = this.redirectIfNeeded.bind(this);
    this.onPrevStep = this.onPrevStep.bind(this);
    this.onNextStep = this.onNextStep.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  getPapUrl(opts) {
    const { categorySlug, subCategorySlug, projectId } = this.props;
    return getPapUrl({
      categorySlug,
      subCategorySlug,
      projectId,
      ...opts
    });
  }

  getCurrentStepIndex() {
    const { questions, stepName } = this.props;
    return getCurrentStepIndex(questions, stepName);
  }

  redirectIfNeeded(questions) {
    const { stepName, lazyRegistering } = this.props;

    /* go to first brief question if none provided */
    if (!stepName) {
      browserHistory.replace(this.getPapUrl({ subStep: questions[0].name }));
    /* go to first step after lazy registration if user has already registered
       and is trying to access that step again */
    } else if (stepName === 'create-account' && !lazyRegistering) {
      const nextStep = questions[LAZY_REGISTRATION_STEP_IDX];
      browserHistory.push(this.getPapUrl({ subStep: nextStep.name }));
    }
  }

  componentDidUpdate(prevProps) {
    const {
      fetchingQuestions, questions, lazyRegistering, fetchDiscountCode, showBanner
    } = this.props;

    if (!fetchingQuestions) {
      this.redirectIfNeeded(questions);
    }

    /* if user just registered, retrieve pending discount code if any so that it's stored
       in the user object */
    if (prevProps.lazyRegistering && !lazyRegistering) {
      const pendingCodeSlug = getPendingDiscountCode(true);
      if (pendingCodeSlug) {
        fetchDiscountCode(pendingCodeSlug).then(({ data }) => {
          /* show banner informing user of discount code */
          const bannerContents = getBannerContents(data, 'apply');
          showBanner(bannerContents.title, bannerContents.content, bannerContents.icon);
        });
      }
    }
  }

  componentDidMount() {
    const {
      fetchCategoriesIfNeeded, fetchBriefQuestions, subCategorySlug, categorySlug,
      setLazyRegistering, user, reset, showBanner, origin, fetchProjectIfNeeded, projectId,
      fetchAttachments, toggleAgencyQuestion
    } = this.props;

    /* make sure form is clean before starting PAP */
    reset();

    trackGoogle("pap", "started pap", origin);

    fetchCategoriesIfNeeded().then(res => {
      /* fetch project or sub-category brief questions depending on whether a
         project ID was provided */
      if (projectId) {
        fetchProjectIfNeeded({ projectId }).then(({ data }) => {
          const subCategory = getSubCategory(res, data.sub_category);
          const category = getCategory(res.data, data.category);

          trackGoogle("pap", "started brief", subCategory.slug);
          trackMixpanel("Started PAP Brief",{
            categorySlug: category.slug,
            subCategorySlug: subCategory.slug
          });
        });

        fetchAttachments(projectId);

      } else {
        const subCategory = getSubCategory(res, subCategorySlug);

        fetchBriefQuestions(subCategory.id).then(res => {
          this.redirectIfNeeded(getVisibleBriefQuestions(res.data));
        });

        trackGoogle("pap", "started brief", subCategorySlug);
        trackMixpanel("Started PAP Brief",{
          categorySlug: categorySlug,
          subCategorySlug: subCategorySlug,
        });
      }
    });

    setLazyRegistering(!user.logged_in);

    toggleAgencyQuestion(!user.profile_data.is_agency_question_answered);

    /* show discount code banner if any */
    const code = getStoredDiscountCode(user.profile_data);
    const bannerContents = getBannerContents(code, 'apply');
    if (bannerContents) {
      showBanner(bannerContents.title, bannerContents.content, bannerContents.icon);
    }
  }

  getPrevStepLink() {
    const { questions } = this.props;
    const prevStepName = questions[this.getCurrentStepIndex() - 1].name;
    return this.getPapUrl({ subStep: prevStepName });
  }

  getNextStepLink(newProjectId) {
    const { questions } = this.props;
    const nextStep = questions[this.getCurrentStepIndex() + 1];
    const nextStepName = nextStep? nextStep.name : '';
    const opts = { subStep: nextStepName };
    if (newProjectId) {
      opts.projectId = newProjectId;
    }
    return this.getPapUrl(opts);
  }

  onPrevStep() {
    const { stepName, questions, submit, categorySlug, subCategorySlug } = this.props;
    const currentStepIndex = this.getCurrentStepIndex();
    let prevStepName;

    /* last step */
    if (currentStepIndex === 0) {
      prevStepName = null;
    } else {
      prevStepName = questions[currentStepIndex - 1].name;
    }

    trackClickWithMixpanel(MP_CHANGED_PAP_STEP_EVENT_NAME, {
      categorySlug: categorySlug,
      subCategorySlug: subCategorySlug,
      fromStep: stepName,
      toStep: prevStepName,
      sectionName: "brief",
      fromIndex: currentStepIndex,
      toIndex: currentStepIndex === 0 ? null : currentStepIndex - 1,
      questionsInSection: questions.length,
      direction: "backward"
    });
    browserHistory.push(this.getPrevStepLink());
  }

  onNextStep() {
    const { stepName, asyncValidate, questions, submit, categorySlug, subCategorySlug } = this.props;
    const currentStepIndex = this.getCurrentStepIndex();
    let nextStepName;

    /* last step */
    if (currentStepIndex === questions.length - 1) {
      nextStepName = null;
    } else {
      nextStepName = questions[currentStepIndex + 1].name;
    }

    trackClickWithMixpanel(MP_CHANGED_PAP_STEP_EVENT_NAME, {
      categorySlug: categorySlug,
      subCategorySlug: subCategorySlug,
      fromStep: stepName,
      toStep: nextStepName,
      sectionName: "brief",
      fromIndex: currentStepIndex,
      toIndex: currentStepIndex === questions.length - 1 ? null : currentStepIndex + 1,
      questionsInSection: questions.length,
      direction: "forward"
    });

    /* are we advancing to the next section? */
    if (!nextStepName)  {
      trackClickWithMixpanel(MP_CHANGED_PAP_SECTION_EVENT_NAME, {
        categorySlug: categorySlug,
        subCategorySlug: subCategorySlug,
        fromSectionName: "brief",
        toSectionName: "details",
        direction: "forward"
      });
    }

    /* if lazy registering, just call `asyncValidate()` to handle account creation */
    if (stepName === "create-account") {
      asyncValidate();
      return;
    }

    /* otherwise, save current step */
    submit();
  }

  handleSubmit(data, _, prevProps) {
    const {
      questions, createProjectRequest, updateProjectRequest, updateAttachments, categories,
      subCategorySlug, setUnhandledError, stepName, lazyRegistering, projectId, attachments,
      updateProjectLocal, categorySlug, fetchAttachments, setBannerError, logout, setAgencySegment
    } = this.props;

    /* prevent submission if hitting Enter key during account creation */
    if (idx(prevProps, _ => _.stepName) === "create-account") {
      return;
    }

    let promise = Promise.resolve();
    let creating = false;

    /* create project on first step where user is logged in and is not on agency question*/
    if (!projectId && !lazyRegistering && stepName !== 'is_agency') {
      const subCategory = getSubCategory({ data: categories }, subCategorySlug);
      creating = true;
      promise = createProjectRequest({ sub_category: subCategory.id }).then((res) => {
        trackGoogle("pap", "created draft", subCategorySlug);
        trackFacebookCustomEvent("1726897664301628", "posted_draft");
        trackPerfectAudience("posted_draft");
        trackPerfectAudience(`posted_${categorySlug.replace(/-/g, '_')}_draft`);
        return res;
      });
    }

    promise = promise.then((res) => {
      /* user not logged in yet: nothing to do */
      if (lazyRegistering) {
        return;
      }

      /* "attachments" step */
      if (stepName === 'file_upload') {
        return updateAttachments(projectId, attachments, data);
      }

      /* agency question step */
      if (stepName === 'is_agency') {
        return setAgencySegment(data.is_agency === 'yes');
      }

      /* all other steps */
      const projectData = {
        brief_answers: omit(data, ['file_upload', 'create-account', 'is_agency']),
      };
      return updateProjectRequest(creating? res.data.id : projectId, projectData);
    });

    return promise.then((res) => {
      const currentStepIndex = this.getCurrentStepIndex();
      let nextUrl;

      /* last step: advance to details section */
      if (currentStepIndex === questions.length - 1) {
        trackGoogle("pap", "started details", subCategorySlug);
        trackFacebookCustomEvent("1726897664301628", "started_details");
        trackMixpanel("Started PAP Details", {
          categorySlug: categorySlug,
          subCategorySlug: subCategorySlug,
        });

        nextUrl = this.getPapUrl({ step: 'details', subStep: 'package' });

      /* otherwise, just proceed to next brief step */
      } else {
        if (questions[currentStepIndex + 1].name === "create-account") {
          trackGoogle("registration", "started registration", "lazy");
          trackMixpanel("Started registration", { "variant": "lazy" });
        }
        nextUrl = this.getNextStepLink(creating? res.data.id : undefined);
      }

      browserHistory.push(nextUrl);

      /* update project locally and fetch attachments if just created */
      if (creating) {
        updateProjectLocal(res.data);
        fetchAttachments(res.data.id);
      }
    }).catch((res) => {
      if (res.status === 403) {
        logout(undefined, true);
        return;
      }

      if (stepName === 'file_upload' && idx(res, _ => _.data.filename[0])) {
        setBannerError('Invalid attachment', idx(res, _ => _.data.filename[0]));
        return;
      }

      setUnhandledError(
        'Error occured while saving the project',
        { response: res, step: `PAP brief (${stepName})` },
        'Please try again.'
      );
    });
  }

  renderStep() {
    const {
      questions, stepName, displayFullScreen, hideFullScreen, fullScreen, setLazyRegistering
    } = this.props;

    const fieldDefinition = questions.find(q => q.name === stepName);

    if (!fieldDefinition) {
      return null;
    }

    const fieldProps = getFieldPropsFromDefinition(fieldDefinition, {
      displayFullScreen: displayFullScreen,
      hideFullScreen: hideFullScreen,
      fullScreen: fullScreen,
      stepNumber: this.getCurrentStepIndex() + 1,
      stepCount: questions.length,
      nextStepLink: this.getNextStepLink(),
      setLazyRegistering,
    });

    return <PAPQuestionBase {...fieldProps} data-transition-id={`brief-${fieldDefinition.name}`} />
  }

  render() {
    const { questions, handleSubmit, fetchingQuestions, stepName, valid, submitting } = this.props;
    const stepContent = this.renderStep();

    return (
      <PAPBase
        hidePrice={true}
        loadingData={fetchingQuestions || !stepName || !stepContent}
        sectionName="brief"
        extraClasses={["brief"]}
        currentStepIndex={this.getCurrentStepIndex()}
        currentSectionStepCount={questions.length}
        onPrevStep={this.onPrevStep}
        onNextStep={this.onNextStep}
        nextEnabled={valid && !submitting}
        prevEnabled={this.getCurrentStepIndex() > 0 && !submitting}
        nextLabel={submitting? 'Saving...' : undefined}
        currentFeatures={[]}
        currentAwards={[]}
        currentTips={[]}
        billables={null}
        {...this.props}>
          <Form onSubmit={handleSubmit(this.handleSubmit)}>
            <PageTransition timeout={500}>{ stepContent }</PageTransition>
          </Form>
      </PAPBase>);
  }
}



// WEBPACK FOOTER //
// ./pap_v2/components/brief.jsx